import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  notification,
  Pagination,
  theme as antdTheme,
  Row,
  Col,
  Transfer,
  Tooltip,
  List,
  Typography,
  Button,
} from 'antd';
import type { TransferDirection } from 'antd/es/transfer';
import { getSignUpFormItems } from 'constants/signUpFormItems';
import CustomTitle from 'components/CustomTitle';
import CustomSelect from 'components/CustomSelect';
import { globalOp } from 'store/ducks/Global';
import GoBackButton from 'components/GoBackButton';
import * as Styled from './Signup.styled';
import { useTranslation } from 'react-i18next';

const { useToken } = antdTheme;
const { Text } = Typography;

interface UserFunction {
  key: React.Key;
  title: string;
  description: string;
  linked_to_user: boolean;
}

export const SignUp: React.FC = () => {
  const { token } = useToken();
  const { Search } = Input;
  const { t } = useTranslation();
  const signUpFormItems = getSignUpFormItems(t);
  const [form] = Form.useForm();
  const [userForms, setUserForms] = useState([0]);
  const [activeFunction, setActiveFunction] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [formData, setFormData] = useState<Record<number, Record<string, any>>>(
    {},
  );
  const [activeUsers, setActiveUsers] = useState<
    { label: string; value: number }[]
  >([]);
  const [users, setUsers] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  const [mockData, setMockData] = useState<UserFunction[]>([]);
  const [targetKeys, setTargetKeys] = useState<React.Key[]>([]);
  const pageSize = 1;
  const [assignKeys, setAssignKeys] = useState<React.Key[]>([]);
  const [removeKeys, setRemoveKeys] = useState<React.Key[]>([]);
  const [activity, setActivity] = useState<{ label: string; value: string }[]>(
    [],
  );
  const [searchText, setSearchText] = useState('');
  const sortedUsers = [...users].sort((a, b) =>
    a.username.localeCompare(b.username),
  );

  const filteredUsers = users
    .filter((user) =>
      user.username.toLowerCase().includes(searchText.toLowerCase()),
    )
    .sort((a, b) => a.username.localeCompare(b.username));

  const handleTransferChange = (
    newTargetKeys: React.Key[],
    direction: TransferDirection,
    moveKeys: React.Key[],
  ) => {
    setTargetKeys(newTargetKeys);

    if (direction === 'right') {
      setAssignKeys((prev) => Array.from(new Set([...prev, ...moveKeys])));
      setRemoveKeys((prev) => prev.filter((key) => !moveKeys.includes(key)));
    } else if (direction === 'left') {
      setRemoveKeys((prev) => Array.from(new Set([...prev, ...moveKeys])));
      setAssignKeys((prev) => prev.filter((key) => !moveKeys.includes(key)));
    }
  };

  const addUserForm = () => {
    if (userForms.length < 20) {
      const newFormIndex = userForms.length;
      setUserForms([...userForms, newFormIndex]);
      setFormData({
        ...formData,
        [newFormIndex]: {},
      });
      setCurrentPage(newFormIndex + 1);
    } else {
      notification.warning({
        message: 'Info',
        description: 'You can only add up to 20 users.',
        duration: 20,
      });
    }
  };

  const removeUserForm = (formIndex: number) => {
    if (userForms.length > 1) {
      const newUserForms = userForms.filter((index) => index !== formIndex);
      const newFormData = { ...formData };
      delete newFormData[formIndex];
      setUserForms(newUserForms);
      setFormData(newFormData);
      setCurrentPage((prev) => Math.min(prev, newUserForms.length));
    } else {
      notification.warning({
        message: '',
        description: 'You must have at least one user form.',
        duration: 20,
      });
    }
  };

  const handleFormChange = (changedValues: any, allValues: any) => {
    const formIndex = Object.keys(changedValues)[0];
    if (formIndex) {
      setFormData({
        ...formData,
        [formIndex]: allValues[formIndex],
      });
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const fetchUserFunctionRoles = async () => {
    try {
      const response = await globalOp.getUserFunctionRoles(activeFunction);
      const data = response?.data || [];
      setUsers(data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchUserFunctionRoles();
  }, [activeFunction]);

  const fetchActiveUsers = async () => {
    try {
      const response = await globalOp.getActiveUsers();
      const formattedOptions = response.map(
        (user: {
          first_name: string;
          id: number;
          last_name: string;
          username: string;
        }) => ({
          label: `${user.username} (${user.first_name} ${user.last_name})`,
          value: user.id,
        }),
      );
      setActiveUsers(formattedOptions);
    } catch (error) {
      console.log('Error fetching:', error);
    }
  };

  const fetchActivity = async () => {
    try {
      const response = await globalOp.getActiveInactiveOptions();
      if (response && response.data) {
        const formattedOptions = response.data.map(
          (option: { label: string; value: boolean }) => ({
            label: option.label,
            value: option.value,
          }),
        );
        setActivity(formattedOptions);
      }
    } catch (error: any) {
      console.log('Failed to fetch activity options:', error);
      notification.error({
        message: 'Error',
        description:
          error?.message ||
          'Something went wrong while fetching activity options.',
      });
    }
  };

  const fetchUserFunction = async () => {
    try {
      const params = { user_id: selectedUser };
      const response = await globalOp.getUserFunctions(params);

      const data: UserFunction[] = response?.data.map((item: any) => ({
        key: String(item.function_id),
        title: item.function_name,
        description: item.description,
        linked_to_user: item.linked_to_user,
      }));

      setMockData(data);

      const defaultTargetKeys = data
        .filter((item) => item.linked_to_user)
        .map((item) => item.key);

      setTargetKeys(defaultTargetKeys);
    } catch (error) {
      console.log('Error fetching user functions:', error);
    }
  };

  const handleSubmit = async () => {
    const usersData = userForms.map((formIndex) => {
      const userFormData = formData[formIndex];
      if (!userFormData) {
        console.log(userFormData, 'userFormData');
        return {};
      }
      return {
        username: userFormData.username,
        password: userFormData.password,
        email: userFormData.email,
        first_name: userFormData.firstName,
        last_name: userFormData.lastName,
        deactivation_date: userFormData.deactivation_date || null,
      };
    });
    try {
      const response = await globalOp.signup(usersData);

      if (response?.results?.[0]?.status === 'success') {
        notification.success({
          message: 'Info',
          description: 'All users have been successfully created.',
          duration: 20,
        });
        setFormData({});
        form.resetFields();
        form.setFieldsValue(
          Object.fromEntries(
            userForms.map((index) => [
              index,
              Object.fromEntries(
                signUpFormItems.map((item) => [item.name, undefined]),
              ),
            ]),
          ),
        );
        // setUserForms([]);
        await fetchUserFunctionRoles();
        fetchActiveUsers();
      } else {
        notification.error({
          message: 'Signup Failed',
          description: 'One or more users could not be created.',
          duration: 10,
        });
      }
    } catch (error: any) {
      console.warn('Caught in handleSubmit:', error.message);
    }
  };

  const toggleStatus = async (username: any) => {
    try {
      const response = await globalOp.userActivityStatus(username);
      const updatedUsers = await globalOp.getUserFunctionRoles(activeFunction);
      setUsers(updatedUsers?.data || []);
    } catch (error) {
      console.log(error);
    }
  };

  const handleSubmitRoles = async () => {
    const user_id = Number(selectedUser);

    if (assignKeys.length > 0) {
      try {
        const function_ids = assignKeys.map((key) => Number(key));
        const response = await globalOp.assignUserFunctions(
          user_id,
          function_ids,
        );
        fetchUserFunctionRoles();
      } catch (error: unknown) {
        console.log('Error assigning functions:', error);
      }
    }

    if (removeKeys.length > 0) {
      try {
        const function_ids = removeKeys.map((key) => Number(key));
        const response = await globalOp.removeUserFunctions(
          user_id,
          function_ids,
        );
        fetchUserFunctionRoles();
      } catch (error: unknown) {
        console.log('Error removing functions:', error);
      }
    }

    setAssignKeys([]);
    setRemoveKeys([]);
  };

  useEffect(() => {
    fetchActiveUsers();
    fetchActivity();
  }, []);

  useEffect(() => {
    fetchUserFunction();
  }, [selectedUser]);

  return (
    <Styled.StyledContainer token={token}>
      <div>
        <GoBackButton
          to="/working_area"
          text={t('USERS.GO_TO_WORKING_AREAS')}
        />
      </div>
      <Row justify="center">
        <Col xs={24} sm={8} md={7}>
          <Styled.FormContainer token={token}>
            <CustomTitle title={t('USERS.ADD_NEW_USERS')} level={3} />
            <Row>
              <Col>
                <Styled.StyledButton
                  token={token}
                  type="primary"
                  size="large"
                  onClick={addUserForm}
                >
                  {t('USERS.ADD_USER')}
                </Styled.StyledButton>
              </Col>
              <Col>
                <Pagination
                  current={currentPage}
                  total={userForms.length}
                  pageSize={pageSize}
                  onChange={handlePageChange}
                />
              </Col>
            </Row>
            <Form
              form={form}
              name="userForm"
              layout="vertical"
              onFinish={handleSubmit}
              autoComplete="off"
              onValuesChange={handleFormChange}
            >
              {userForms
                .slice((currentPage - 1) * pageSize, currentPage * pageSize)
                .map((formIndex, index) => (
                  <Styled.InputForm key={formIndex}>
                    <CustomTitle title={`${formIndex + 1}`} level={4} />
                    {signUpFormItems.map((item) => (
                      <Styled.StyledFormItem
                        token={token}
                        key={`${item.name}_${formIndex}`}
                        label={item.label}
                        name={[formIndex, item.name]}
                        dependencies={
                          item.name === 'confirmPassword'
                            ? [[formIndex, 'password']]
                            : undefined
                        }
                        rules={
                          item.name === 'confirmPassword'
                            ? [
                                {
                                  required: true,
                                  message: t('USERS.SELECT_STATUS'),
                                },
                                {
                                  validator: (_, value) => {
                                    if (
                                      !value ||
                                      form.getFieldValue([
                                        formIndex,
                                        'password',
                                      ]) === value
                                    ) {
                                      return Promise.resolve();
                                    }
                                    return Promise.reject(
                                      new Error('Passwords do not match'),
                                    );
                                  },
                                },
                              ]
                            : item.rules
                        }
                      >
                        {item.type === 'password' ? (
                          <Input.Password visibilityToggle />
                        ) : item.type === 'custom' && item.render ? (
                          item.render()
                        ) : (
                          <Input type={item.type} />
                        )}
                      </Styled.StyledFormItem>
                    ))}

                    <Styled.StyledButton
                      token={token}
                      type="primary"
                      size="large"
                      onClick={() => removeUserForm(formIndex)}
                    >
                      {t('USERS.REMOVE_ERRONEOUS_USER')}
                    </Styled.StyledButton>
                  </Styled.InputForm>
                ))}
              <Row>
                <Col>
                  <Styled.StyledButton
                    token={token}
                    type="primary"
                    size="large"
                    htmlType="submit"
                  >
                    {t('GENERAL.SAVE')}
                  </Styled.StyledButton>
                </Col>
              </Row>
            </Form>
          </Styled.FormContainer>
        </Col>
        <Col xs={24} sm={16} md={7}>
          <Styled.FormContainer token={token}>
            <CustomTitle title={t('USERS.MANAGE_USER_ACCESS')} level={3} />
            <Styled.CustomWrapper>
              <CustomSelect
                options={activeUsers}
                value={selectedUser}
                onChange={(value: React.SetStateAction<number | null>) =>
                  setSelectedUser(value)
                }
                placeholder={t('USERS.SELECT_USER')}
                filterOption={(input: string, option: { label: string }) =>
                  option?.label?.toLowerCase().includes(input.toLowerCase())
                }
              />
              {selectedUser && (
                <Tooltip title={t('USERS.CLEAR_SELECTION')}>
                  <Styled.CloseCircleOutlinedIcon
                    onClick={() => setSelectedUser(null)}
                  />
                </Tooltip>
              )}
            </Styled.CustomWrapper>

            <Styled.ActiveUsers>
              {selectedUser && (
                <>
                  <Transfer
                    dataSource={mockData}
                    titles={[
                      t('USERS.NOT_ACTIVE_FUNCTIONS'),
                      t('USERS.ACTIVE_FUNCTIONS'),
                    ]}
                    targetKeys={targetKeys}
                    onChange={handleTransferChange}
                    render={(item) => item.title}
                    locale={{ itemUnit: '', itemsUnit: '' }}
                  />
                  <Styled.SubmitContainer>
                    <Styled.StyledButton
                      token={token}
                      type="primary"
                      onClick={handleSubmitRoles}
                      disabled={
                        assignKeys.length === 0 && removeKeys.length === 0
                      }
                    >
                      {t('GENERAL.SAVE')}
                    </Styled.StyledButton>
                  </Styled.SubmitContainer>
                </>
              )}
            </Styled.ActiveUsers>
          </Styled.FormContainer>
        </Col>
        <Col xs={24} sm={12} md={7}>
          <Styled.FormContainer token={token}>
            <CustomTitle title={t('USERS.VIEW_USER_RIGHTS')} level={5} />
            <CustomSelect
              options={activity}
              value={activeFunction}
              onChange={(value: boolean) => setActiveFunction(value)}
              placeholder={t('USERS.SELECT_STATUS')}
              filterOption={(input: string, option?: { label: string }) =>
                option?.label?.toLowerCase().includes(input.toLowerCase())
              }
            />
            <Search
              placeholder={t('USERS.SEARCH_USER')}
              allowClear
              onChange={(e) => setSearchText(e.target.value)}
            />
            <Styled.ViewUsersContainer token={token}>
              <List
                itemLayout="horizontal"
                dataSource={filteredUsers}
                bordered
                renderItem={(user) => {
                  const functionNames =
                    user.functions?.map(
                      (func: { function_name: any }) => func.function_name,
                    ) || [];
                  const roleNames =
                    user.functions?.flatMap((func: { roles: any[] }) =>
                      func.roles.map((role) => role.role_name),
                    ) || [];
                  return (
                    <List.Item>
                      <List.Item.Meta
                        title={<Text strong>{user.username}</Text>}
                        description={
                          <>
                            <Styled.UsersData token={token}>
                              <Text>{user.email}</Text>
                              <Text>
                                {t('USERS.FUNCTION_NAME')}
                                {functionNames.length > 1 ? 's' : ''}:{' '}
                                {functionNames.join(', ')}
                              </Text>
                            </Styled.UsersData>
                            <Styled.UsersData token={token}>
                              <Text type="secondary">
                                {t('USERS.ROLE')}
                                {roleNames.length > 1 ? 's' : ''}:{' '}
                                {roleNames.join(', ')}
                              </Text>
                            </Styled.UsersData>
                            <Button
                              type="primary"
                              danger={user.active}
                              onClick={() => toggleStatus(user.username)}
                            >
                              {activeFunction
                                ? t('USERS.INACTIVATE_USER')
                                : t('USERS.ACTIVATE_USER')}
                            </Button>
                          </>
                        }
                      />
                    </List.Item>
                  );
                }}
              />
            </Styled.ViewUsersContainer>
          </Styled.FormContainer>
        </Col>
      </Row>
    </Styled.StyledContainer>
  );
};

export default SignUp;
